/*
  Small building blocks for the project detail tabs (UI overhaul, wave 1), on top of the app foundation
  (tokens, StatusPill, Button). Kept here so ProjectDetailPage.js does not grow further.
*/
import { forwardRef, useEffect, useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { toneClass } from '@/lib/status';

/** Codes (part numbers, VO-002, DN123, device addresses): JetBrains Mono even inside the Apple-skinned page. */
export const MONO = "[font-family:'JetBrains_Mono',ui-monospace,SFMono-Regular,Menlo,monospace] tracking-normal";

/**
 * Width of an element (ResizeObserver), for layouts that depend on the space a card really has (the sidebar
 * and side columns make the viewport a poor guide). null until measured, or where it cannot be (jsdom).
 */
export function useElementWidth(ref) {
  const [width, setWidth] = useState(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      const next = Math.round(node.getBoundingClientRect().width);
      if (next > 0) setWidth((current) => (current === next ? current : next));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/** Card surface used by the new project-detail blocks. */
export const Panel = forwardRef(({ className, as: Comp = 'section', ...props }, ref) => (
  <Comp ref={ref} className={cn('rounded-card border border-line bg-surface text-ink shadow-card', className)} {...props} />
));
Panel.displayName = 'Panel';

export function PanelHead({ title, meta, actions, className, titleAs: Title = 'h3' }) {
  return (
    <div className={cn('flex min-h-[52px] flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-line px-4 py-2.5 sm:px-5', className)}>
      <div className="flex min-w-0 items-center gap-2">
        <Title className="truncate text-[14px] font-bold text-ink">{title}</Title>
        {meta ? <span className="text-[12px] text-ink-3">{meta}</span> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Tab toolbar: title + one muted sentence on the left, actions on the right (one navy primary). */
export function TabHeader({ title, subtitle, actions, className, id }) {
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-3', className)}>
      <div className="min-w-0">
        <h2 id={id} className="font-heading text-[18px] font-extrabold leading-tight text-ink">{title}</h2>
        {subtitle ? <p className="mt-1 text-[13px] text-ink-3">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

const BAR_FILL = {
  navy: 'bg-[color:var(--brand)]',
  green: 'bg-status-green',
  amber: 'bg-status-amber',
  red: 'bg-status-red',
  blue: 'bg-status-blue',
  violet: 'bg-status-violet',
  lime: 'bg-[color:var(--lime-600,#6aa603)]',
  gray: 'bg-[color:var(--status-gray)]',
};

/** Thin progress bar (role="progressbar"); value 0–100. */
export function ProgressBar({ value, tone = 'navy', className, label, height = 6 }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      className={cn('block overflow-hidden rounded-full bg-[color:var(--status-gray-bg)]', className)}
      style={{ height }}
    >
      <span className={cn('block h-full rounded-full transition-[width]', BAR_FILL[tone] || BAR_FILL.navy)} style={{ width: `${pct}%` }} />
    </span>
  );
}

/** Segmented control: one of N views, each with an optional count. */
export function Segmented({ options, value, onChange, ariaLabel, className, size = 'md' }) {
  return (
    <div role="group" aria-label={ariaLabel} className={cn('inline-flex max-w-full shrink-0 gap-0.5 overflow-x-auto rounded-[11px] bg-[color:var(--status-gray-bg)] p-[3px]', className)}>
      {options.map((option) => {
        const active = option.key === value;
        const zero = option.count === 0;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            data-testid={option.testId}
            data-state={active ? 'on' : 'off'}
            title={option.title}
            onClick={() => onChange(option.key)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              size === 'sm' ? 'h-[26px] px-2 text-[12.5px]' : 'h-[30px] text-[13px]',
              active ? 'bg-surface text-ink shadow-card' : 'text-ink-2 hover:text-ink'
            )}
          >
            {option.dot ? <span className={cn('ui-tone-dot', toneClass(option.dot))} aria-hidden="true" /> : null}
            <span className={option.mono ? MONO : undefined}>{option.label}</span>
            {option.count !== undefined && option.count !== null ? (
              <span className={cn('text-[11.5px] font-semibold tabular-nums', zero ? 'text-ink-4' : 'text-ink-3')}>
                {formatNumber(option.count, { maxDecimals: 0 })}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

const CALLOUT_TONES = {
  red: 'border-[color:var(--danger-border,#f3c2c2)] bg-status-red-bg text-ink',
  amber: 'border-[color:color-mix(in_srgb,var(--status-amber)_40%,transparent)] bg-status-amber-bg text-ink',
  violet: 'border-line bg-surface text-ink',
  lime: 'border-[color:var(--lime-300,#b8e26b)] bg-[color:var(--lime-50,#f6fcea)] text-ink',
  navy: 'border-line bg-surface text-ink',
};

/** Inline callout: tinted icon tile + text + actions on the right. */
export function Callout({ tone = 'navy', icon: Icon, children, actions, className, role = 'status', ...props }) {
  return (
    <div
      role={role}
      className={cn('flex flex-wrap items-center gap-x-4 gap-y-3 rounded-card border px-4 py-3 shadow-card sm:px-5', CALLOUT_TONES[tone] || CALLOUT_TONES.navy, className)}
      {...props}
    >
      {Icon ? (
        <span className={cn('ui-icon-tile h-9 w-9', toneClass(tone === 'navy' ? 'navy' : tone))} aria-hidden="true">
          <Icon className="h-[18px] w-[18px]" />
        </span>
      ) : null}
      {/* At least ~14rem for the text: on a phone the actions wrap below it instead of squeezing it to one word a line. */}
      <div className="min-w-[min(100%,14rem)] flex-1 text-[13.5px] leading-snug text-ink-2">{children}</div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Small rounded count chip ("3 fl · 46"). Forwarded ref so it can be a Tooltip trigger. */
export const CountChip = forwardRef(({ className, children, tone, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'inline-flex h-[22px] shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-line bg-surface-2 px-1.5 text-[11.5px] font-semibold tabular-nums text-ink-2',
      tone === 'navy' && 'border-transparent bg-[color:var(--brand)] text-[color:var(--brand-fg)]',
      className
    )}
    {...props}
  >
    {children}
  </span>
));
CountChip.displayName = 'CountChip';

/** Label over a big number (summary strips). */
export function MiniStat({ label, value, meta, className, valueClassName }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-[11.5px] font-semibold text-ink-3">{label}</div>
      <div className={cn('font-heading text-[20px] font-extrabold leading-tight tabular-nums text-ink', valueClassName)}>{value}</div>
      {meta ? <div className="mt-0.5 text-[12px] text-ink-3">{meta}</div> : null}
    </div>
  );
}

const menuItemClass = 'relative flex min-h-[34px] cursor-default select-none items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] text-ink outline-none transition-colors focus:bg-surface-hover data-[highlighted]:bg-surface-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:h-[15px] [&>svg]:w-[15px] [&>svg]:shrink-0 [&>svg]:text-ink-3';

/**
 * Dropdown menu with your own trigger (a Button). Items like RowActions:
 * { label, icon, onSelect, disabled, hint, shortcut, description, testId, danger, separatorBefore, heading }.
 * Falsy items are skipped. onSelect runs after the menu has closed (safe to open dialogs).
 */
export function ActionMenu({ trigger, items = [], align = 'end', contentClassName, onOpenChange, label }) {
  const visible = items.filter(Boolean);
  return (
    <DropdownMenuPrimitive.Root modal={false} onOpenChange={onOpenChange}>
      <DropdownMenuPrimitive.Trigger asChild>{trigger}</DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align={align}
          sideOffset={6}
          collisionPadding={8}
          aria-label={label}
          className={cn(
            'z-50 min-w-[220px] max-w-[340px] overflow-hidden rounded-xl border border-line bg-surface p-1.5 text-ink shadow-overlay',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            contentClassName
          )}
        >
          {visible.map((item, index) => {
            if (item.heading) {
              return (
                <DropdownMenuPrimitive.Label key={`heading-${item.heading}`} className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-3">
                  {item.heading}
                </DropdownMenuPrimitive.Label>
              );
            }
            const Icon = item.icon;
            return [
              item.separatorBefore && index > 0 ? (
                <DropdownMenuPrimitive.Separator key={`sep-${item.key || item.label}`} className="my-1 h-px bg-line-2" />
              ) : null,
              <DropdownMenuPrimitive.Item
                key={item.key || item.label}
                disabled={item.disabled}
                data-testid={item.testId}
                className={cn(menuItemClass, item.danger && 'text-status-red-ink data-[highlighted]:bg-status-red-bg [&>svg]:text-status-red-ink')}
                onSelect={() => {
                  if (!item.onSelect) return;
                  setTimeout(() => item.onSelect(), 0);
                }}
              >
                {Icon ? <Icon aria-hidden="true" /> : null}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{item.label}</span>
                  {item.description ? <span className="block text-[11.5px] leading-snug text-ink-3">{item.description}</span> : null}
                </span>
                {item.hint ? <span className="ml-3 shrink-0 text-[11.5px] font-medium text-ink-3">{item.hint}</span> : null}
                {item.shortcut ? <kbd className="ui-kbd ml-3 shrink-0">{item.shortcut}</kbd> : null}
              </DropdownMenuPrimitive.Item>,
            ];
          })}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
